import { createContext, ReactNode, useCallback, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate } from "react-router";
import { decodeToken, isExpiringSoon } from "../functions/helper";
import { triggerTokenRefresh } from "../api/Auth.api";
import Loading from "../components/Loading";

export type AuthProviderProps = {
  children: ReactNode
}

export type Auth = {
  iat: number
  exp: number
  roles: any[]
  email: string
  id: number
  accno: number
  token: string
  refresh_token: string
  is_active: boolean
  stop_credit: boolean
}

export type AuthContextType = {
  isLoading: boolean
  auth: Auth
  persist: any
  handleAuthentication: (auth: Auth) => void
  handlePersist: (value: string) => void
  handleLogout: () => void
}

const AuthContext = createContext({} as AuthContextType);

const LoginRoute = '/login';

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [cookies, setCookie, removeCookie] = useCookies([
    'auth-token', 'auth-refresh-token', 'persist'
  ]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [auth, setAuth] = useState<Auth>({} as Auth);
  const [persist, setPersist] = useState<string>(cookies.persist ?? '0');

  function getDefaultCookieOptions(auth: Auth): object {
    const now = Math.floor(Date.now() / 1000);
    return {
      path: '/',
      ...(auth && {
        maxAge: auth.exp - now
      })
    }
  }

  const handleAuthentication = useCallback((auth: Auth) => {
    try {
      const options = getDefaultCookieOptions(auth);
      setAuth(auth);
      setCookie('auth-token', auth.token, options);
      setCookie('auth-refresh-token', auth.refresh_token, {
        ...options, maxAge: 7200 // 2 hours in seconds
      });
    } catch (error) {
      console.log(error);
    }
  }, [])

  const handlePersist = useCallback((value: string) => {
    setPersist(value);
    setCookie('persist', value);
  }, [])

  const handleLogout = useCallback(() => {
    setAuth({} as Auth);
    removeCookie('auth-token');
    removeCookie('auth-refresh-token');
    removeCookie('persist');
  }, [])

  useEffect(() => {
    try {
      const authToken = cookies["auth-token"]
      const refreshToken = cookies["auth-refresh-token"]
      const isPersist = cookies["persist"]

      if (!authToken && !refreshToken) {
        handleLogout()
        setIsLoading(false);
        navigate(LoginRoute, { replace: true, state: { from: location } });
        return;
      }

      if (!!isPersist && refreshToken && (!authToken || isExpiringSoon(authToken))) {
        triggerTokenRefresh()
          .then((data) => {
            const auth = decodeToken(data.token);

            // Check if user is still active after token refresh
            if (!auth.is_active) {
              handleLogout();
              navigate(LoginRoute, { replace: true, state: { from: location } });
              return;
            }

            handleAuthentication({ ...auth, token: data.token, refresh_token: data.refreshToken });
          })
          .finally(() => setIsLoading(false));
        return;
      }

      const decodedAuth = decodeToken(authToken);

      // Check if user is active when loading from existing token
      if (!decodedAuth.is_active) {
        handleLogout();
        setIsLoading(false);
        navigate(LoginRoute, { replace: true, state: { from: location } });
        return;
      }

      setAuth({ ...decodeToken(authToken), token: authToken, refresh_token: refreshToken });
      setIsLoading(false);
    } catch (error) {
      console.log(error);
    }
  }, [navigate, cookies])

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        auth,
        persist,
        handleAuthentication,
        handlePersist,
        handleLogout
      }}>
      {isLoading ? <Loading /> : children}
    </AuthContext.Provider>
  )
}

export default AuthContext;